import { useState } from "react";
import { Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { useLinkJiraIssueScoped, type JiraParentType } from "@/hooks/useJira";

interface Props {
  projectId: string;
  parentType?: JiraParentType;
  allowedKeys: string[];
  onClose: () => void;
}

/**
 * Modal for pasting a Jira key or URL and linking it to the active
 * project or initiative. Validation happens server-side (the link
 * endpoint rejects project keys outside JIRA_PROJECT_KEYS); we only
 * do a quick client-side regex check so the user sees obvious typos
 * before the round-trip.
 */
export function JiraLinkDialog({ projectId, parentType = "project", allowedKeys, onClose }: Props) {
  const [value, setValue] = useState("");
  const [linkedBy, setLinkedBy] = useState("");
  const link = useLinkJiraIssueScoped(parentType, projectId);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!value.trim()) return;
    try {
      const result = await link.mutateAsync({
        issue_key_or_url: value.trim(),
        linked_by: linkedBy.trim() || undefined,
      });
      toast.success(`Linked ${result.issue.issue_key}`);
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? err?.message ?? "Link failed";
      toast.error(msg);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl bg-surface shadow-xl ring-1 ring-line"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-line-soft px-5 py-3">
          <h2 className="text-sm font-semibold text-ink">Link Jira issue</h2>
          <button
            onClick={onClose}
            className="rounded p-1 text-ink-subtle hover:bg-raised hover:text-ink-3"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-4 p-5">
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-3">
              Issue key or URL
            </label>
            <input
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="DEV-1234 or https://etedevops.atlassian.net/browse/DEV-1234"
              className="w-full rounded-lg border border-line px-3 py-2 text-sm focus:border-brand-navy-500 focus:outline-none focus:ring-1 focus:ring-brand-navy-500"
            />
            <p className="mt-1 text-xs text-ink-muted">
              Allowed projects: {allowedKeys.join(", ")}
            </p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ink-3">
              Linked by (optional)
            </label>
            <input
              value={linkedBy}
              onChange={(e) => setLinkedBy(e.target.value)}
              placeholder="Your name"
              className="w-full rounded-lg border border-line px-3 py-2 text-sm focus:border-brand-navy-500 focus:outline-none focus:ring-1 focus:ring-brand-navy-500"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-3 py-1.5 text-sm text-ink-3 hover:bg-raised"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!value.trim() || link.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-navy-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {link.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Link
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
